import {Box, Grid, Typography} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useRef, useState} from 'react';
import {ReactComponent as AddIcon} from '../../../assests/svg/plus-icon-success.svg';
import {
  BUDGET_ESTIMATED,
  CLIENT_PERMISSION_CODES_CONSTANTS,
  ONLY_DATE_SHOW_FORMAT,
  PERMISSION_GROUP_CATEGORY,
} from '../../../constants/constants';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import styles from '../../../styles/components/userManagement/clientInfoTab.module.scss';
import {PermissionGroup} from '../../../types/permission-group/permissionGroup';
import {ClientProject} from '../../../types/project-type/projectDetailsType';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import HostCountryDetails from '../../aur-host-country-details/AurHostCountryDetails';
import AurAccordion, {
  AurAccordionRef,
} from '../../common/aur-accordion/AurAccordion';
import AurButton from '../../common/aur-button/AurButton';
import AurIconButton from '../../common/aur-button/AurIconButton';
import AurHighlyConfidential from '../../common/aur-highly-confidential/AurHighlyConfidential';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {ProjectDetails} from '../../pages/user-management/models';
import {ClientDetailsInfo} from './ClientAndProjectFormContainer';
import PermissionGroupDialog from '../../permission-groups/PermissionGroupDialog';
import {Permission} from '../../../types/permission-group/permission';
import ClientProjectSelection from '../add-new-team-member/ClientProjectSelection';
import {addNewOrUpdateProjectsToUserRole} from '../../../api/ClientManagementService';
import {getPermissionDetail} from '../../pages/user-management/tabs/utils';
import AurPopup from '../../common/aur-popup/AurPopup';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  AddNewOrUpdateProjectsToUserRoleReq,
  ClientManagementApiResult,
  resolveClientManagementCommandDto,
} from '../../../types/client-management/cientManagementTypes';
import {UserRolesData} from '../../../redux/slices/userRoleSlice';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {ItemType} from '../../pages/user-management/tabs/AddClientInfo';
import PermissionGroupDrawer from '../../permission-groups/PermissionGroupDrawer';
import _ from 'lodash';
import {useSelector} from 'react-redux';
import {resolvePermissionGroupItemType} from '../../../utils/permissionGroupItemUtils';
import {permissionGroupSelector} from '../../../redux/slices/permissionGroupSlice';
import {RootState} from '../../../redux/store';
import {useHasClientPermission} from '../../../hooks/permissions/usePermissionsByClientId';

type ClientAccordionViewContainerProps = {
  clientInfoDetails: ClientDetailsInfo;
  isControlDisabled?: boolean;
  permissionFromDB?: Permission[];
  onEditButtonClicked: (id: number) => void;
  onDeleteButtonClicked: (id: number) => void;
  onProjectSavedOrCanceled?: (savedUserRole?: UserRolesData) => void;
  mode?: string;
  showHCProjects: boolean;
  clientProjects: ClientProject[];
  clientHCProjects: ClientProject[];
  selectedPermissionGroup?: PermissionGroup | null;
  selectedClientId?: string;
  onEditProject?: (id: number, isEdit: boolean) => void;
  permissionGroupOptionList?: ItemType[];
  projectPermissionGroupOptionList?: ItemType[];
  onPermissionGroupChange?: (permissionGroupId: string) => void;
  onPermissionGroupsUpdated?: (savedPermissionGroup?: PermissionGroup) => void;
  onAssignProjectClick?: () => void;
};

const ClientAccordionViewContainer = (
  props: ClientAccordionViewContainerProps
) => {
  const {
    clientInfoDetails,
    isControlDisabled = false,
    onEditButtonClicked,
    onDeleteButtonClicked,
    onProjectSavedOrCanceled,
    mode,
    showHCProjects,
    clientProjects,
    clientHCProjects,
    selectedPermissionGroup,
    permissionFromDB,
    onEditProject,
    permissionGroupOptionList,
    projectPermissionGroupOptionList,
    onPermissionGroupChange,
    onPermissionGroupsUpdated,
    onAssignProjectClick,
  } = props;
  const {showToast} = useToast();
  const permissionGroupsState = useSelector(permissionGroupSelector);
  const accordionRef = useRef<AurAccordionRef>(null);
  const [openProjectForm, setOpenProjectForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projects, setProjects] = useState<ProjectDetails[]>(
    clientInfoDetails.projectList || []
  );
  const [permCategory, setPermCategory] = useState(
    PERMISSION_GROUP_CATEGORY.CLIENT
  );
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedProjectForEdit, setSelectedProjectForEdit] =
    useState<ProjectDetails>();
  const [editProjectPermission, setEditProjectPermission] = useState(false);
  const [disableControl, setDisableControl] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedPermissionGroupDetails, setSelectedPermissionGroupDetails] =
    useState<ItemType>();
  const [permissionGroupOpen, setPermissionGroupOpen] =
    useState<boolean>(false);
  const [category, setCategory] = useState<PERMISSION_GROUP_CATEGORY>(
    PERMISSION_GROUP_CATEGORY.PROJECT
  );
  const loggedInUserEmail = useSelector(
    (state: RootState) => state.user.user?.email
  );
  const {selectedUserEmailAddress} = useSelector(
    (state: RootState) => state.teamMember
  );

  const hasManageUsersPermission = useHasClientPermission(
    clientInfoDetails.clientId,
    CLIENT_PERMISSION_CODES_CONSTANTS.USERS_VIEW
  );

  useEffect(() => {
    setDisableControl(
      openProjectForm || editProjectPermission || isControlDisabled
    );
  }, [openProjectForm, editProjectPermission, isControlDisabled]);

  const closeProjectFormUi = () => {
    setOpenProjectForm(false);
    setEditProjectPermission(false);
  };

  const onCancelProjectClicked = () => {
    closeProjectFormUi();
    onProjectSavedOrCanceled?.();
  };

  const getSavedUserRoleFromResponse = (response: {
    data?: {data?: ClientManagementApiResult<UserRolesData> | UserRolesData};
  }): UserRolesData | undefined => {
    const dto = resolveClientManagementCommandDto<UserRolesData>(
      response?.data?.data
    );
    return dto?.userRoleId ? dto : undefined;
  };

  useEffect(() => {
    setProjects(clientInfoDetails.projectList || []);
  }, [clientInfoDetails.projectList]);

  const openPermissionGroupDetails = (
    permissionGroupId: string | undefined,
    category: PERMISSION_GROUP_CATEGORY
  ) => {
    const permissionGroupsList =
      category === PERMISSION_GROUP_CATEGORY.CLIENT
        ? permissionGroupOptionList
        : projectPermissionGroupOptionList;
    setSelectedPermissionGroupDetails(
      resolvePermissionGroupItemType(
        permissionGroupId,
        permissionGroupsList,
        permissionGroupsState.permissionGroups
      )
    );
    setPermCategory(category);
    setShowPermissionModal(true);
  };

  const getPermissionGroupDescription = (
    permissionGroupId: string | undefined
  ) => {
    return permissionGroupOptionList?.find(
      (item) => item.ItemKey == permissionGroupId
    )?.ItemDescription;
  };

  const saveProject = async (
    data: AddNewOrUpdateProjectsToUserRoleReq,
    successMessage: string,
    errorMessage: string
  ) => {
    setShowLoader(true);
    try {
      const response = await addNewOrUpdateProjectsToUserRole(
        clientInfoDetails.userRoleId ?? '',
        [data]
      );
      const savedUserRole = getSavedUserRoleFromResponse(response);
      setShowLoader(false);
      showToast(successMessage, 'success', undefined, 'center');
      onProjectSavedOrCanceled?.(savedUserRole);
      onEditProject && onEditProject(clientInfoDetails.id as number, false);
      closeProjectFormUi();
    } catch {
      setShowLoader(false);
      showToast(errorMessage, 'error', undefined, 'center');
    }
  };

  const addProjectHandler = (
    projectDetails: ProjectDetails,
    successMessage: string,
    errorMessage: string
  ) => {
    const permissions: Permission[] = [
      ...(projectDetails.projectPermissions ?? []),
    ].filter(Boolean);

    if (projectDetails.permissionCodeBudgetConsumption === true) {
      permissions?.push(
        getPermissionDetail(
          BUDGET_ESTIMATED.REPORT_BUDGET_CONSUMPTION,
          permissionFromDB
        ) as never
      );
    }
    if (projectDetails.permissionCodeBudgetAlert === true) {
      permissions?.push(
        getPermissionDetail(
          BUDGET_ESTIMATED.REPORT_BUDGET_ALERT,
          permissionFromDB
        ) as never
      );
    }
    saveProject(
      {
        id: projectDetails.projectId,
        name: '',
        domainCountryCode: '',
        dataCenterName: '',
        projectPermissions: permissions,
        workspaces: _.uniqBy(projectDetails.workspaces, 'workspaceId').map(
          (w) => {
            return typeof w.isDeleted === 'boolean'
              ? {
                  id: w.workspaceId,
                  name: w.workspaceName,
                  isDeleted: w.isDeleted,
                }
              : {
                  id: w.workspaceId,
                  name: w.workspaceName,
                };
          }
        ),
        permissionGroupId: projectDetails.permissionGroupId
          ? projectDetails.permissionGroupId
          : null,
        clientId: clientInfoDetails?.clientId,
      },
      successMessage,
      errorMessage
    );
  };

  const removeFromSavedProjectList = (projectId: string) => {
    const requestdata = {
      id: projectId,
      name: '',
      isDeleted: true,
      workspaces: [],
      permissionGroupId: clientInfoDetails?.projectList?.find(
        (project) => project.projectId === projectId
      )?.permissionGroupId,
      clientId: clientInfoDetails?.clientId,
    };

    saveProject(
      requestdata,
      t('addEditTeamMember.form.removeProjectSuccess'),
      t('addEditTeamMember.form.removeProjectFailed')
    );
    setShowDeleteModal(false);
  };

  const titleContent = (
    <>
      {!disableControl && (
        <Grid item xs="auto" className="client-list-icon">
          <AurIconButton
            size="big"
            shape
            dataTestId="client-edit"
            icon={<AurIcon icon={IconNames.Edit} />}
            onClick={() => onEditButtonClicked(clientInfoDetails.id as number)}
            ariaLabel={t('buttons.edit')}
          />
        </Grid>
      )}
    </>
  );

  const adminNote = (
    <Grid container className="client-accordion-view-row">
      <Grid item xs={9} className="form-content">
        <Box className={`${styles.reviewTabAdminText}`}>
          <Typography className="client-info-profile-note-text">
            {t('addEditTeamMember.form.clientAdminContentAccess')}
            <strong>{t('addEditTeamMember.form.projectAndWorkspaces')}</strong>
            {t('addEditTeamMember.form.withClientinAurora')}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  const projectListContent = (
    <Grid container className="project-list-content-row">
      <Grid item xs={2}>
        <Typography className="typo-text subhead4_size">
          {t('addEditTeamMember.form.projectLabel')}
        </Typography>
      </Grid>
      <Grid item xs={10}>
        {projects && projects.length > 0
          ? projects?.map((item) => {
              return (
                <Box key={item.projectId}>
                  <Box sx={{paddingBottom: '30px'}} className="project-details">
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Box className="typo-text subhead2_size">
                        {item.projectName}
                      </Box>
                      {!disableControl &&
                        loggedInUserEmail != selectedUserEmailAddress &&
                        hasManageUsersPermission && (
                          <Grid item xs="auto" className="client-list-icon">
                            <AurIconButton
                              size="big"
                              shape
                              dataTestId="project-edit"
                              ariaLabel={t('buttons.edit')}
                              icon={<AurIcon icon={IconNames.Edit} />}
                              onClick={() => {
                                setEditProjectPermission(true);
                                setSelectedProjectForEdit(item);
                                if (item.permissionGroupId) {
                                  onPermissionGroupChange?.(
                                    item.permissionGroupId
                                  );
                                }
                                onEditProject &&
                                  onEditProject(
                                    clientInfoDetails.id as number,
                                    true
                                  );
                              }}
                            />
                          </Grid>
                        )}
                    </Box>
                    {item.highlyConfidential ? (
                      <AurHighlyConfidential
                        size="small"
                        className="client-list-highly-confidential"
                        type="pill"
                        showWarningText={true}
                      />
                    ) : (
                      <></>
                    )}
                    <HostCountryDetails
                      countryName={item.countryName}
                      dataCenterName={item.dataCenterName}
                      domainCountryCode={item.domainCountryCode}
                    />
                    {(!editProjectPermission ||
                      item.projectId !== selectedProjectForEdit?.projectId) &&
                      item.workspaces.length > 0 && (
                        <Box className="workspaces-list">
                          <Typography className="typo-text label1_size">
                            {t('addEditTeamMember.form.workspacesLabel')}
                          </Typography>

                          {_.uniqBy(item.workspaces, 'workspaceId').map(
                            (workspace) => (
                              <Typography
                                className="typo-text body2_size mist_1400"
                                key={workspace.workspaceCode}
                              >
                                {workspace?.workspaceCode
                                  ? `${workspace.workspaceCode} - ${workspace.workspaceName}`
                                  : workspace.workspaceName}
                              </Typography>
                            )
                          )}
                        </Box>
                      )}
                    {(!editProjectPermission ||
                      item.projectId !== selectedProjectForEdit?.projectId) &&
                      !clientInfoDetails.isAdmin && (
                        <Grid item xs={10} sx={{paddingTop: '24px'}}>
                          <Typography className="typo-text subhead4_size">
                            {item?.permissionGroupName}
                          </Typography>
                          {item?.permissionGroupId && (
                            <AurButton
                              ariaLabel={t(
                                'addEditTeamMember.form.permissionGroupDetails'
                              )}
                              disabled={false}
                              onClick={() =>
                                openPermissionGroupDetails(
                                  item.permissionGroupId,
                                  PERMISSION_GROUP_CATEGORY.PROJECT
                                )
                              }
                              type="text"
                              size="Small"
                              label={t(
                                'addEditTeamMember.form.permissionGroupDetails'
                              )}
                              classNames="remove-padding"
                            />
                          )}
                        </Grid>
                      )}
                    {editProjectPermission &&
                      item.projectId === selectedProjectForEdit?.projectId && (
                        <Box className="edit-project-container">
                          <ClientProjectSelection
                            hideProjectDropdown
                            sectionTitleHidden={true}
                            clientProjects={clientProjects}
                            clientHCProjects={clientHCProjects}
                            unsavedProjects={projects}
                            deleteProjectButtonClicked={() =>
                              setShowDeleteModal(true)
                            }
                            onAddThisProjectClick={(projectDetails) => {
                              addProjectHandler(
                                projectDetails,
                                t(
                                  'addEditTeamMember.form.updateProjectSuccess'
                                ),
                                t('addEditTeamMember.form.updateProjectFailed')
                              );
                              setEditProjectPermission(false);
                            }}
                            onCancelClick={() => {
                              onCancelProjectClicked();
                              onEditProject &&
                                onEditProject(
                                  clientInfoDetails.id as number,
                                  false
                                );
                            }}
                            showHCProjects={showHCProjects}
                            isAdmin={clientInfoDetails.isAdmin}
                            selectedClientBudgetEstimatedPermisionEnable={
                              clientInfoDetails.budgetConsumptionEnabled
                            }
                            defaultSelectedProject={item}
                            permissionOptionList={
                              projectPermissionGroupOptionList
                            }
                            onCreateNewPermissionGroup={() => {
                              setCategory(PERMISSION_GROUP_CATEGORY.PROJECT);
                              setPermissionGroupOpen(true);
                            }}
                            onPermissionChange={onPermissionGroupChange}
                            selectedClientId={clientInfoDetails.clientId}
                          />
                        </Box>
                      )}
                  </Box>
                </Box>
              );
            })
          : !clientInfoDetails.isAdmin && (
              <Typography className={`typo-text body3_size no-project-text`}>
                {t('addEditTeamMember.form.noProjectsAssigned')}
              </Typography>
            )}
      </Grid>
    </Grid>
  );

  const bodyContent = (
    <>
      {mode === 'view' && (
        <>
          <Grid container className="role-list-row">
            <Grid item xs={2}>
              <Typography className="typo-text subhead4_size">
                {t('addEditTeamMember.form.roleLabel')}
              </Typography>
            </Grid>
            <Grid
              item
              xs={10}
              display={'flex'}
              justifyContent={'space-between'}
            >
              <Typography className="typo-text subhead4_size">
                {clientInfoDetails.role}
              </Typography>
              {loggedInUserEmail != selectedUserEmailAddress &&
                hasManageUsersPermission &&
                titleContent}
            </Grid>
            {!clientInfoDetails.isAdmin && (
              <>
                <Grid item xs={2}>
                  <Typography className="typo-text subhead4_size">
                    {t('addEditTeamMember.form.clientPermissionGroupLabel')}
                  </Typography>
                </Grid>
                <Grid item xs={10} className={`${styles.clientPermDetailsBox}`}>
                  <Typography className="typo-text subhead4_size">
                    {clientInfoDetails.permissionGroup}
                  </Typography>
                  <Typography
                    className={`typo-text body2_size ${styles.permDescriptionText}`}
                  >
                    {getPermissionGroupDescription(
                      clientInfoDetails.permissionGroupId
                    )}
                  </Typography>
                  <AurButton
                    ariaLabel={t(
                      'addEditTeamMember.form.clientPermissionGroupDetails'
                    )}
                    disabled={false}
                    onClick={() =>
                      openPermissionGroupDetails(
                        clientInfoDetails.permissionGroupId,
                        PERMISSION_GROUP_CATEGORY.CLIENT
                      )
                    }
                    type="text"
                    size="Small"
                    label={t(
                      'addEditTeamMember.form.clientPermissionGroupDetails'
                    )}
                    classNames="remove-padding"
                  />
                </Grid>
              </>
            )}
          </Grid>
          {clientInfoDetails.isAdmin && adminNote}
        </>
      )}
      {(!clientInfoDetails.isAdmin || (projects && projects.length > 0)) && (
        <>
          {projectListContent}
          <Grid container className="client-accordion-view-row assign-project">
            <Grid item xs={2}></Grid>
            <Grid item xs={10} className="item">
              {openProjectForm && (
                <ClientProjectSelection
                  clientProjects={clientProjects}
                  clientHCProjects={clientHCProjects}
                  unsavedProjects={clientInfoDetails.projectList}
                  onAddThisProjectClick={(projectDetails) =>
                    addProjectHandler(
                      projectDetails,
                      t('addEditTeamMember.form.addProjectSuccess'),
                      t('addEditTeamMember.form.adddProjectFailed')
                    )
                  }
                  onCancelClick={() => {
                    onCancelProjectClicked();
                    onEditProject &&
                      onEditProject(clientInfoDetails.id as number, false);
                  }}
                  showHCProjects={showHCProjects}
                  isAdmin={clientInfoDetails.isAdmin}
                  selectedClientId={clientInfoDetails?.clientId}
                  selectedClientBudgetEstimatedPermisionEnable={
                    clientInfoDetails.budgetConsumptionEnabled
                  }
                  projectTitle={t(
                    'addEditTeamMember.form.assignProjectHeader',
                    {clientName: clientInfoDetails.client}
                  )}
                  buttonLabel={t('addEditTeamMember.form.addProject')}
                  permissionOptionList={projectPermissionGroupOptionList}
                  selectedPermissionGroup={selectedPermissionGroup}
                  onCreateNewPermissionGroup={() => {
                    setCategory(PERMISSION_GROUP_CATEGORY.PROJECT);
                    setPermissionGroupOpen(true);
                  }}
                  onPermissionChange={onPermissionGroupChange}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}
      {clientInfoDetails.isAdmin ||
      showHCProjects ||
      !clientInfoDetails.isAdmin ? (
        <>
          <Grid container className="client-accordion-view-row">
            <Grid item xs={2}></Grid>
            <Grid item xs={10}>
              {!disableControl &&
                loggedInUserEmail != selectedUserEmailAddress &&
                hasManageUsersPermission && (
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    {(!clientInfoDetails.isAdmin || showHCProjects) && (
                      <AurButton
                        ariaLabel="addEditTeamMember.form.buttonAssignProject"
                        disabled={false}
                        onClick={() => {
                          onAssignProjectClick && onAssignProjectClick();
                          setOpenProjectForm(true);
                          onEditProject &&
                            onEditProject(clientInfoDetails.id as number, true);
                        }}
                        type="exit"
                        startIcon={<AddIcon aria-hidden="true" />}
                        size="Medium"
                        dataTestId="assign-project"
                        label={t('addEditTeamMember.form.buttonAssignProject')}
                      />
                    )}

                    <AurButton
                      ariaLabel="Remove Client"
                      disabled={false}
                      type="exit"
                      startIcon={
                        <AurIcon
                          icon={IconNames.Remove}
                          color={IconColors.Primary}
                          size="small"
                        />
                      }
                      onClick={() => {
                        setEditProjectPermission(false);
                        onDeleteButtonClicked(clientInfoDetails.id as number);
                      }}
                      label={t('addEditTeamMember.form.buttonRemoveClient')}
                      classNames="remove-client-button"
                      size="Medium"
                      dataTestId="remove-client"
                    />
                  </Box>
                )}
            </Grid>
          </Grid>
          <Grid container className="client-accordion-view-row claccord2">
            <Grid item xs={2}></Grid>
            <Grid item xs={10}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <div className="aur-user-creator-details-container">
                  <Typography className={`typo-text label2_size`}>
                    {t('addEditTeamMember.form.createdBy', {
                      createdBy: clientInfoDetails.createdBy,
                    })}
                    <AurLightTooltip title={t('common.yearMonthDate')}>
                      <span className="text-padding">
                        {getUTCDateTimeForDisplay(
                          clientInfoDetails.createdDate ?? new Date(),
                          ONLY_DATE_SHOW_FORMAT
                        )}
                      </span>
                    </AurLightTooltip>
                  </Typography>
                  {clientInfoDetails.updatedBy && (
                    <Typography className={`typo-text label2_size`}>
                      {t('addEditTeamMember.form.updatedBy', {
                        updatedBy: clientInfoDetails.updatedBy,
                      })}
                      <AurLightTooltip title={t('common.yearMonthDate')}>
                        <span className="text-padding">
                          {getUTCDateTimeForDisplay(
                            clientInfoDetails.updatedDate ?? new Date(),
                            ONLY_DATE_SHOW_FORMAT
                          )}
                        </span>
                      </AurLightTooltip>
                    </Typography>
                  )}
                </div>
              </Box>
            </Grid>
          </Grid>
        </>
      ) : (
        <></>
      )}
    </>
  );

  return (
    <>
      {mode === 'view' ? (
        <AurAccordion
          fullWidth
          title={clientInfoDetails.client}
          ref={accordionRef}
          alertWarningIcon={false}
          className="client-details"
          disabled={isControlDisabled}
        >
          {bodyContent}
        </AurAccordion>
      ) : (
        bodyContent
      )}
      <PermissionGroupDialog
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        permissionModalData={selectedPermissionGroupDetails}
        permCategory={permCategory}
      />
      {showDeleteModal && (
        <AurPopup
          showCloseIcon
          showPopupTitle
          showPopupHeading
          open={showDeleteModal}
          className="profile-delete-modal aur-unsaved-changes-popup"
          onOk={() =>
            removeFromSavedProjectList(selectedProjectForEdit?.projectId || '')
          }
          onCancel={() => setShowDeleteModal(false)}
          popupTitle={t('common.userProfile.deletePopup.title')}
          popupHeading={t('common.userProfile.projectDeletePopup.heading')}
          popupContent={t('common.userProfile.projectDeletePopup.content')}
          labelActionCancel={t('common.userProfile.deletePopup.cancelBtn')}
          labelActionOk={t('common.userProfile.deletePopup.deleteBtn')}
        />
      )}
      <AurLoadingIndicator overlay={true} isOpen={showLoader} />
      {permissionGroupOpen && (
        <PermissionGroupDrawer
          shouldShow={permissionGroupOpen}
          onClose={() => setPermissionGroupOpen(false)}
          clientId={clientInfoDetails?.clientId ?? ''}
          budgetConsumptionEnabled={false}
          clientName={''}
          onUpdate={(savedPermissionGroup) => {
            onPermissionGroupsUpdated?.(savedPermissionGroup);
          }}
          showAddGrpOnOpen={true}
          category={category}
        />
      )}
    </>
  );
};

export default ClientAccordionViewContainer;
